import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { DocumentGridStateType, ISaveFocus } from "../../../@types/documents";
import { DocumentStatusType, DocumentType, IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { TabsPanel } from "../../../components/tabs";
import { IInvoiceGetDTO, IInvoiceViewDTO } from "../../../libs/coreapi-dto/documents/invoice";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { InvoiceDataProvider } from "../../../Services/DataProviders/InvoiceDataProvider";
import { KizDocDataProvider } from "../../../Services/DataProviders/KizDocDataProvider";
import useGridFilter, { DefaultDateGridFilter, DefaultGridFilter } from "../../../system/hooks/useGridFilter";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { UserActionLogModal } from "../../Service/UserActionLog/view/UserActionLogModal";
import styles from "../../styles/index.module.scss";
import { ConfirmationItemsModal } from "../../../components/modalWindows/ConfirmationItemsModal";
import { ProgressActionSpinner } from "../../../components/progressActionSpinner/ProgressActionSpinner";
import { ProcessWarningModalWindow } from "../../../components/modalWindows/ProcessWarningModalWindow";
import { CorrectSumModal } from "./CorrectSumModal/CorrectSumModal";
import DefaultDocumentsCommandsPanelV3 from "../../../components/commandsPanels/DefaultDocumentsCommandsPanelV3";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import { ICreateDocumentFromItemsRequest, IInvoiceItemGetDTO } from "../../../libs/coreapi-dto/documents/invoiceItem";
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import { ReviceKizDocModalWindow } from "../../../components/modalWindows/ReviceKizDocModalWindow";
import { TourStep } from "../../../components/tour/Tour";
import { useTourEducation } from "../../../components/tour/TourContext";
import { getInvoiceDocumentCommandsPanelTourSteps, getInvoicePluginViewTourSteps } from "../../../components/tour/TourSteps/InvoicePluginViewTourSteps";
import { InvoiceItemsSelectorModal } from "./InvoiceItem/InvoiceItemsSelectorModal";
import { SupplierDataChecksDataProvider } from "../../../Services/DataProviders/SupplierDataChecksDataProviders";
import { SupplierDataCheckAction } from "../../../libs/coreapi-dto/dirs/supplierDataCheck";
import { useProducerPriceValidation } from "./Components/ProducerPriceValidation/useProducerPriceValidation";
import ProducerPriceValidationModal from "./Components/ProducerPriceValidation/ProducerPriceValidationModal";

interface IErrors {
    itemName: string
    producerPrice: number
    registerPrice: number
    message: string
}

interface IBooleanSettings {
    supplierUseVat: boolean,
    sysOptIsVat22: boolean
}

const InvoicePluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const pluginCtx = usePluginContext();

    const invoiceDP = new InvoiceDataProvider(appContext.coreApiService);
    const kizDocDP = new KizDocDataProvider(appContext.coreApiService);
    const supplierDataCheckDP = new SupplierDataChecksDataProvider(appContext.coreApiService);
    const tabCtx = useTabsContext();
    const { registerTour } = useTourEducation();
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");

    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.InProcess);

    const [data, setData] = useState<IInvoiceViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [totalSums, setTotalSums] = useState<ISumsInfoViewDTO | undefined>();
    const [showDetailsTabs, setShowDetailsTabs] = useState<boolean>(false);

    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);
    const [_, setIdGlobalInvoice] = useLocalStorageChange<string>('idGlobalInvoice');

    //status type for invoice in gridfilter
    const [documentStatusType, setDocumentStatusType] = useState<DocumentStatusType | undefined>();
    const [selectedDocumentState, setSelectedDocumentState] = useState<DocumentStatusType | undefined>();
    const [multipleSelect, setMultipleSelect] = useState<boolean>(false);
    // one selected items in invoice
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemView, setSelectedItemViews] = useState<IInvoiceViewDTO>();

    const [showInvoiceItemSelectorModal, setShowInvoiceItemSelectorModal] = useState<boolean>(false);

    // selected items in invoice for multiselect
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);

    //
    const [impossibilityProcess, setImpossibilityProcess] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    //forModalLog
    const [showModalLog, setShowModalLog] = useState<boolean>(false);

    const [showModalCorrectSum, setShowModalCorrectSum] = useState<boolean>(false);
    const [showModalReviseKizDoc, setShowModalReviseKizDoc] = useState<boolean>(false);
    const [modalWindow, setModalWindow] = useState(<></>);

    // Refs и шаги для tour этой вкладки.
    const [documentCommandsPanelTourSteps, setDocumentCommandsPanelTourSteps] = useState<TourStep[]>([]);
    const invoiceGridTourRef = useRef<HTMLDivElement>(null);
    const invoiceDetailsTabsTourRef = useRef<HTMLDivElement>(null);
    const currentIdTab = useRef(tabCtx.currentTab?.id);

    const [saveFocus, setSaveFocus] = useState<ISaveFocus>({
        viewState: 'view',
        selectedItem: selectedItem,
        data: data
    });

    const [repeatInfoGismtEnabled, setRepeatInfoGismtEnabled] = useState<boolean>(false);

    const [isItemUpdating, setIsItemUpdating] = useState(false);
    const { validateProducerPriceVsRegisterPrice, modalConfig } = useProducerPriceValidation();
    const isValidatedRef = useRef(false);
    
    const { t } = useTranslation();

    useEffect(() => {
        setIsSubmitting(true);
        invoiceDP.getViewParam(gridFilter, async (data, totalCount, sums) => {
            setData(data);
            setTotalCount(totalCount);
            setTotalSums(sums);
            if (viewState == "refresh") setViewState("view");
            if (totalCount === 0) {
                setLoadingStatus(LoadingStatus.NoData);
            } else {
                setLoadingStatus(LoadingStatus.Completed);
            }
            setIsSubmitting(false);
        });
    }, [gridFilter, viewState]);

    useEffect(() => {
        if (tabCtx.currentTab?.title === t("sidePanel.documents.invoices")) {
            setShowDetailsTabs(true);
        } else {
            setShowDetailsTabs(false);
        }
        pluginCtx.masterGrid.onUnselectEvent();
        setShowDetailsTabs(true);
    }, [tabCtx.currentTab]);

    useEffect(() => {
        if (!multipleSelect) {
            setSelectedItems([]);
        } else {
            setSelectedItem(undefined);
        }
        if (selectedItem) {
            setIsItemUpdating(true);

            const documentState = selectedItem.cells.find((c) => c.propertyName === "documentState")?.value as DocumentStatusType;
            setSelectedDocumentState(documentState ?? undefined);

            invoiceDP.getView(filterOneItem, (docs: IInvoiceViewDTO[], totalCount) => {
                if (totalCount === 1) {
                    setSelectedItemViews(docs[0]);
                }
                setIsItemUpdating(false);
            });

            invoiceDP.repeatInfoGismtEnabled(selectedItem.idGlobal, (result: boolean) => {
                setRepeatInfoGismtEnabled(result && documentState === DocumentStatusType.Prov);
            })

        } else {
            setSelectedItemViews(undefined);
            setIsItemUpdating(false);
            setRepeatInfoGismtEnabled(false);
        }
    }, [multipleSelect, selectedItem]);

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent();
    }, [documentStatusType, gridFilter, viewState]);

    const filterOneItem = {
        numberPerPage: 1,
        pageNumber: 1,
        columnFilters: [
            {
                name: "IdGlobal",
                operator: 'Eq',
                value: selectedItem?.idGlobal
            }
        ],
    } as IGridFilter;

    const deleteRow = () => {
        if (selectedItem?.[0]?.isDeleted || selectedDocumentState === "save" || selectedDocumentState === "proc") {
        } else {
            invoiceDP.markDelete(selectedItem?.idGlobal as string, () => {
                setViewState("refresh");
            });
        }
    };

    const [сonfirmationItemsModalJsx, setConfirmationItemsModalJsx] = useState<JSX.Element>(<></>);
    const renderConfirmationModal = (header: string, warning: string, items: string, question, cancel: () => void) => {
        setConfirmationItemsModalJsx(
            <ConfirmationItemsModal
                header={`${header}`}
                warning={ `${warning}` }
                listPositions={`${items}`}
                question={`${question}`}
                cancel={cancel}
            />
        );
    }

    const handleOpenActReturnToContractor = async () => {
        if (!selectedItemView) {
            console.error('Данные документа не выбраны');
            return;
        } else {
            tabCtx.openChild(
                'act_return_to_contractor_create_plugin',
                'create', 
                uuidv4(),                
                () => { },
                selectedItemView?.mnemocode
            );
            setIdGlobalInvoice(selectedItemView?.idGlobal)           
        }
    };

    const handleReviseKizDoc = async () => {
         if (!selectedItemView) {
            console.error('Данные документа не выбраны');
            return;
        } else {
            setShowModalReviseKizDoc(true)
        }
    }

    const [progressActionSpinnerJsx, setProgressActionSpinnerJsx] = useState<JSX.Element>(<></>);
    const manageProgressView = (actionMessage?: string, show = false) => {
        if (show) {
            setProgressActionSpinnerJsx(
                <ProgressActionSpinner
                    loaderText={actionMessage}
                />
            );
        } else {
            setProgressActionSpinnerJsx(<></>);
        }
    };

    async function create702DocumentType3Async() {
        manageProgressView(t("documents.importRemains.sendingScheme702Type3"), true);
        try {
            await new Promise<string>((resolve, reject) => {
                kizDocDP.create702DocumentType3(selectedItem?.idGlobal as string, (e) => {
                    if (e.respType === 'isFailed') {
                        reject(e.message?.title)
                    } else {
                        resolve(e.message?.text)
                    }
                })
            })
            renderConfirmationModal('Успешно!', '', t("documents.importRemains.scheme702Type3hasBeenSent"), '', () => setConfirmationItemsModalJsx(<></>));

        } catch (error) {
            if (typeof error === 'string') {
                renderConfirmationModal(t("documents.importRemains.problemSending702Type3Scheme"), '', error, '', () => setConfirmationItemsModalJsx(<></>));
            }
        }
        finally {
            manageProgressView();
        }
    }

    type RepeatSendMethod = 'createRepeatSendDocument' | 'createRepeatSendPackingInfo' | 'createRepeatSendInfoGismt';

    async function createRepeatSendAsync(
        method: RepeatSendMethod,
        idGlobal: string,
        successMessageFull: string,
        successMessagePartial: string
    ) {
        manageProgressView(t("general.sendingRepeatDocument"), true);
        try {
            let result: boolean | null = false;

            await new Promise<string>((resolve, reject) => {
                kizDocDP[method](idGlobal, (e) => {
                    if (e.respType === 'isFailed') {
                        reject(e.message?.title);
                    } else {
                        result = e.data;
                        resolve(e.message?.text);
                    }
                });
            });

            const message = result ? successMessageFull : successMessagePartial;
            renderConfirmationModal('Успешно!', '', message, '', () => setConfirmationItemsModalJsx(<></>));

        } catch (error) {
            if (typeof error === 'string') {
                renderConfirmationModal(t("general.problemSendingRepeatDocument"), '', error, '', () => setConfirmationItemsModalJsx(<></>));
            }
        } finally {
            manageProgressView();
        }
    }

    async function createRepeatSendDocumentAsync() {
        await createRepeatSendAsync(
            'createRepeatSendDocument',
            selectedItem?.idGlobal as string,
            t("general.sendingRepeatDocumentHasBeenSent"),
            t("general.sendingRepeatDocumentHasBeenSent")
        );
    }

    async function createRepeatSendPackingInfoAsync() {
        await createRepeatSendAsync(
            'createRepeatSendPackingInfo',
            selectedItem?.idGlobal as string,
            t("general.sendingRepeatDocumentHasBeenSent"),
            t("general.sendingRepeatPartHasBeenSent")
        );
    }

    async function createRepeatSendInfoGismtAsync() {
        await createRepeatSendAsync(
            'createRepeatSendInfoGismt',
            selectedItem?.idGlobal as string,
            t("general.sendingRepeatDocumentHasBeenSent"),
            t("general.sendingRepeatPartHasBeenSent")
        );
    }

    const getInvoice = (): Promise<IInvoiceGetDTO | null> => {
        return new Promise((resolve, reject) => {
            const selectedItemIdGlobal = selectedItem?.idGlobal;
            if (!selectedItemIdGlobal) {
                return;
            }
            try {
                invoiceDP.getById(selectedItemIdGlobal as string, async (invoiceDocument) => {
                    resolve(invoiceDocument);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    const validateBeforeProcess = (callback: (result: boolean) => void) => {
        
        if (isValidatedRef.current) {
            callback(true);
            return;
        }

        isValidatedRef.current = false;
        
        const filter = {
            ...DefaultGridFilter,
            columnFilters: [
                {
                    name: "mnemocode",
                    operator: "Eq",
                    value: "PRODUCER_PRICE_GT_REGISTER_PRICE_4_IMPORTANT_GOODS"
                } as IGridColumnFilter
            ]
        };

        supplierDataCheckDP.getView(filter, (data) => {
            const supplierDataCheckAction = data?.[0]?.action as SupplierDataCheckAction || "NONE";

            if (supplierDataCheckAction === "NONE") {
                isValidatedRef.current = true;
                callback(true);
                return;
            }

            getInvoice()
                .then((invoiceData) => {
                    const errors = validateProducerPriceVsRegisterPrice(
                        invoiceData?.items || [],
                        supplierDataCheckAction,
                        {
                            onCancel: () => {
                                isValidatedRef.current = false;
                                callback(false);
                            },
                            onConfirm: () => {
                                isValidatedRef.current = true;
                                callback(true);
                            }
                        }
                    );
                    
                    if (errors.length === 0) {
                        isValidatedRef.current = true;
                        callback(true);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    isValidatedRef.current = false;
                    callback(false);
                });
        });
    };

    const initStateForValidate = () => {
        isValidatedRef.current = false;
    };

    const requestDoValidationSuccessedAlready = () => {
        const isValid = isValidatedRef.current;
        return isValid;
    };

    // Собираем общий сценарий tour для журнала ПН.
    const invoiceTourSteps = useMemo<TourStep[]>(() => {
        return getInvoicePluginViewTourSteps({
            grid: invoiceGridTourRef,
            detailsTabs: invoiceDetailsTabsTourRef,
        }, documentCommandsPanelTourSteps)
    }, [documentCommandsPanelTourSteps])

    // Регистрируем tour, чтобы кнопка обучения в QuickAccessBar стала активной.
    useEffect(() => {
        return registerTour({
            plugin: "invoice_plugin",
            tabId: currentIdTab.current,
            steps: invoiceTourSteps,
        })
    }, [registerTour, invoiceTourSteps])

    return (
        <>
            <PluginWrapper>
                <ToolbarWrapper>                   
                    <DefaultDocumentsCommandsPanelV3
                        selectedItems={[selectedItems,setSelectedItems]}
                        selectedItem={[selectedItem, setSelectedItem]}
                        saveFocus={[saveFocus, setSaveFocus]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        plugin="invoice_creator_plugin"
                        documentStatusType={[documentStatusType, setDocumentStatusType]}
                        selectedDocumentStatusType={[selectedDocumentState, setSelectedDocumentState]}
                        multipleSelect={[multipleSelect, setMultipleSelect]}
                        dataProvider={invoiceDP}
                        pluginSettings={props.plugin}
                        idTable={IdTableVariant.Invoice}
                        setViewState={(vs) => setViewState(vs)}
                        optionsForSearchPanel={[
                            { displayName: 'Содержит товар', value: 'goodsName' },
                            { displayName: 'Аптека', value: 'contractorName' },
                        ]}
                        advancedFeatures
                        impossibilityAction={{ impossibilityProcess }}
                        isValidateProc={validateBeforeProcess}
                        requestDoValidationSuccessedAlready={requestDoValidationSuccessedAlready}
                        initStateForValidate={initStateForValidate}
                        items={[
                            {
                                label: t("documents.importRemains.scheme702"),
                                clueText: t("documents.importRemains.clueTextScheme702"),
                                onClick: () => {
                                    if (selectedItemView?.isMdlp702SchemeAllowed) {

                                        (async function () {
                                            manageProgressView(t("documents.importRemains.sendingScheme702"), true);
                                            try {
                                                await new Promise<string>((resolve, reject) => {
                                                    kizDocDP.create702Document(selectedItem?.idGlobal as string, (e) => {
                                                        if (e.respType === 'isFailed') {
                                                            reject(e.message?.title)
                                                        } else {
                                                            resolve(e.message?.text)
                                                        }
                                                    })
                                                })
                                                renderConfirmationModal('Успешно!', '', t("documents.importRemains.scheme702hasBeenSent"), '', () => setConfirmationItemsModalJsx(<></>));

                                            } catch (error) {
                                                if (typeof error === 'string') {
                                                    renderConfirmationModal(t("documents.importRemains.problemSending702Scheme"), '', error, '', () => setConfirmationItemsModalJsx(<></>));
                                                }
                                            }
                                            finally {
                                                manageProgressView();
                                            }
                                        })();
                                    }
                                },
                                disabled: selectedItemView?.isMdlp702SchemeAllowed ? false : true,
                            },
                            {
                                label: t("documents.importRemains.scheme702Type3"),
                                clueText: t("documents.importRemains.clueTextScheme702Type3"),
                                onClick: () => {
                                    if (selectedItemView?.isMdlp702SchemeAllowed) {

                                        kizDocDP.warningCreate702Document(selectedItem?.idGlobal as string, 3, async (e) => {
                                            if (e && e.respType === "isCompleted")
                                            {
                                                if (e.data && e.data > 30)
                                                {
                                                    setModalWindow(
                                                        <ProcessWarningModalWindow
                                                            //${t("documents.importRemains.scheme702Type3youSure")}: ${responseContractorTo}
                                                          text={`  
                                                          ${t("documents.importRemains.scheme702Type3youSure")}. ${t("general.confirm")}`}
                                                          cancel={() => {
                                                            setModalWindow(<></>)
                                                            return
                                                          }}
                                                          process={async () => {
                                                            setModalWindow(<></>)
                                                            await create702DocumentType3Async();
                                                            return
                                                          }}
                                                        />
                                                      );
                                                }
                                                else {
                                                    await create702DocumentType3Async();
                                                }
                                            }
                                            else {
                                                await create702DocumentType3Async();
                                            }
                                        })
                                        //()();
                                    }
                                },
                                disabled: !selectedItemView?.isMdlp702SchemeAllowed,
                            },
                            {
                                label: t("documents.invoice.repeatSendMdlp"),  //"Отправить повторно в МДЛП",
                                clueText: t("documents.invoice.clueTextRepeatSendMdlp"),
                                onClick: async () => {
                                    if (selectedItemView?.isRepeatSendAllowed) {
                                        await createRepeatSendDocumentAsync();
                                    }
                                },
                                disabled: !selectedItemView?.isRepeatSendAllowed,
                            },
                            {
                                label: t("documents.invoice.repeatSendPackingInfoMdlp"),  //"Отправить повторно в МДЛП пакинг инфо",
                                clueText: t("documents.invoice.clueTextRepeatSendPackingInfoMdlp"),
                                onClick: async () => {
                                    if (selectedItemView?.isRepeatSendPackingInfoAllowed) {
                                        await createRepeatSendPackingInfoAsync();
                                    }
                                },
                                disabled: !selectedItemView?.isRepeatSendPackingInfoAllowed,
                            },
                            {
                                label: t("documents.invoice.repeatSendInfoGismt"),  //"Отправить повторно в ГИС МТ",
                                clueText: t("documents.invoice.clueTextRepeatSendInfoGismt"),
                                onClick: async () => {
                                    if (repeatInfoGismtEnabled) {
                                        await createRepeatSendInfoGismtAsync();
                                    }
                                },
                                disabled: !repeatInfoGismtEnabled,
                            },
                            {
                                label: "Просмотреть лог пользователя",
                                onClick: () => selectedItem && setShowModalLog(true),
                                disabled: selectedItem ? false : true,
                            },
                            {
                                label: t("documents.invoice.correctSum.header"),
                                onClick: () => selectedItem && setShowModalCorrectSum(true),
                                disabled: (selectedItem ? false : true) || selectedItem?.isDeleted,
                            },
                            {
                                label: t("documents.invoice.createDepositedCheque"),
                                onClick: () => {
                                    invoiceDP.createDepositedCheque(selectedItem?.idGlobal as string, (id) => {
                                        renderConfirmationModal('Отложенный чек создан', '', id, '', () => setConfirmationItemsModalJsx(<></>));
                                    })
                                },
                                disabled: (selectedItem ? false : true) || selectedItem?.isDeleted || selectedItemView?.documentState !== "proc",
                            },
                            {
                                label: t("documents.invoice.createActReturnToContractor"),
                                onClick: () => handleOpenActReturnToContractor(),
                                disabled: (selectedItem ? false : true) || selectedItem?.isDeleted || selectedItemView?.documentState !== "proc" || isItemUpdating,
                            },
                            {
                                label: t("documents.invoice.createReturnActForProblematicRows"),
                                onClick: () => setShowInvoiceItemSelectorModal(true),
                                disabled: (selectedItem ? false : true) || selectedItem?.isDeleted || selectedItemView?.documentState !== "proc" || isItemUpdating,
                            },
                            {
                                label: t("documents.invoice.reviseKizDoc"),
                                onClick: () => handleReviseKizDoc(),
                                disabled: (selectedItem ? false : true) || selectedItem?.isDeleted,
                            }
                        ]}
                        mnemocode={selectedItemView?.mnemocode}
                        checkLockingDocuments
                        /* Шаги tour внутри общей панели команд документов. */
                        tourSteps={getInvoiceDocumentCommandsPanelTourSteps}
                        onTourStepsChange={setDocumentCommandsPanelTourSteps}
                    />
                </ToolbarWrapper>
                
                <div className={styles.gridWrapper}>
                    {/* Target для tour: основная таблица документов. */}
                    <div className={styles.masterGrid} ref={invoiceGridTourRef}>
                        {isSubmitting ? (
                            <Spinner />
                        ) : (
                            <DefaultGrid
                                autoSelect
                                openWithEnterForEdit={{
                                pluginTabContext:"invoice_creator_plugin",
                                mnemocode:selectedItemView?.mnemocode,
                                selectedDocumentState}}
                                gridId={props.gridId}
                                data={data}
                                documentStatus
                                showQuantity
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                separator
                                actionGridRow={{ delete: () => deleteRow() }}
                                filter={gridFilter}
                                markingStatus
                                hiddenPagination={undefined}
                                dataProvider={invoiceDP}
                                totalCount={totalCount}
                                sums={totalSums}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectedItem={selectedItem}
                                selectedItems={selectedItems}
                                saveFocus={[saveFocus, setSaveFocus]}
                                isServerFilter
                                searching
                                getView={(gridFilter,callback) => {
                                    invoiceDP.getViewParam(gridFilter, (entities) => {
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                getStyleRow={(item) => {
                                    const cell = item.cells.find(x => x.propertyName === 'internetOrderNumber');
                                    return ((cell?.value !== undefined) && (cell?.value !== null) && (cell.value !== '')) ? styles.internetOrderNumberCells : null;
                                }}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                    row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.invoice) : pluginCtx.masterGrid.onUnselectEvent();
                                }}
                                onMultipleSelect={(rows) => {
                                    setSelectedItems(rows);
                                }}
                                onSort={(i) => {
                                    dispatchGridFilter({ type: "sort", payload: i.propertyName });
                                }}
                                onFilterDelete={(i) => {
                                    i.propertyName === "documentState" && setDocumentStatusType(undefined);
                                    dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                                }}
                                onPageNumberChange={(n) => {
                                    dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } });
                                }}
                                onNumberPerPageChange={(n) => {
                                    dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } });
                                }}
                            />
                        )}
                    </div>
                    {
                        <div className={styles.detailsTabView}>
                            {showDetailsTabs && detailsTabsPanel.tabs.length > 0 && selectedItem && !multipleSelect && (
                                <>
                                    <div className={tabsStyles.tabsPanelWrapper}>
                                        {/* Target для tour: панель детализации выбранного документа. */}
                                        <div id="detailsTabsPanelWrapper" ref={invoiceDetailsTabsTourRef} style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                            <TabsPanel
                                                id="detailsTabsPanel"
                                                activeId={detailsTabsPanel.currentTab?.id}
                                                tabs={detailsTabsPanel.tabs}
                                                onActive={(id) => {
                                                    dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                }}
                                            ></TabsPanel>
                                        </div>
                                    </div>
                                    <div className={tabsStyles.contentWrapper}>
                                        {detailsTabsPanel.tabs.map((item) => {
                                            return (
                                                <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                    {React.cloneElement(item.view.content, {key: item.id, documentId: selectedItem?.idGlobal})}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    }
                    {showModalLog && <UserActionLogModal idDocumentGlobal={selectedItem?.idGlobal} onClick={() => setShowModalLog(false)} />}
                    {showModalCorrectSum && <CorrectSumModal idDocumentGlobal={selectedItem?.idGlobal as string}
                    
                    ok={() => { 
                        setShowModalCorrectSum(false)
                        setViewState("refresh")
                    }}
                    cancel={() => {
                        setShowModalCorrectSum(false)
                    }} />}

                    {showModalReviseKizDoc && 
                        <ReviceKizDocModalWindow
                            idDocumentGlobal={selectedItem?.idGlobal as string}
                            ok={() => { 
                                setShowModalReviseKizDoc(false)
                                setViewState("refresh")
                            }}
                            idTable={IdTableVariant.Invoice}
                            cancel={() => {
                                setShowModalReviseKizDoc(false)
                            }} 
                        />}

                        {showInvoiceItemSelectorModal && (
                            <InvoiceItemsSelectorModal 
                                idGlobalInvoice={selectedItem?.idGlobal as string}
                                multipleSelect={true}
                                hasDefectMultipleSelect={true}
                                gridFilter={{
                                    fieldOrderBy: "hasDefects",
                                    orderDesc: true,
                                    numberPerPage: 10000,
                                    pageNumber: 1
                                } as IGridFilter}
                                ok={(entities) => {
                                    if (entities.length == 0)  setShowInvoiceItemSelectorModal(false)
                    
                                    const request = {
                                        idInvoiceItemsGlobal: entities.map(item => item.idGlobal),
                                        IdTable: IdTableVariant.ActReturnToContractor
                                    } as ICreateDocumentFromItemsRequest
                    
                                    invoiceDP.сreateDocumentFromItems(request, (response) => {
                                        tabCtx.openChild(
                                            'act_return_to_contractor_create_plugin',
                                            'edit',
                                            response.idGlobal,
                                            () => { },
                                            response?.mnemocode
                                        );
                                        setShowInvoiceItemSelectorModal(false)
                                    })
                                    
                                }} 
                                cancel={() => setShowInvoiceItemSelectorModal(false)}
                            />
                        )}

                    {modalWindow}
                </div>
                {progressActionSpinnerJsx}
                {сonfirmationItemsModalJsx}
                {modalConfig && (
                    <ProducerPriceValidationModal
                        visible={modalConfig.visible}
                        header={modalConfig.header}
                        warning={modalConfig.warning}
                        items={modalConfig.items}
                        question={modalConfig.question}
                        action={modalConfig.action}
                        onConfirm={modalConfig.onConfirm}
                        onCancel={modalConfig.onCancel}
                        largeTextArea={modalConfig.largeTextArea}
                        textAreaInputStyle={modalConfig.textAreaInputStyle}
                    />
                )}
            </PluginWrapper>
        </>
    );
};

export default InvoicePluginView;
